import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, Search, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { PlayerSearch } from "./PlayerSearch";
import { SocialRow } from "./Social";
import { NAV_GROUPS as DRAWER } from "../lib/navigation";

export { DRAWER };

export const Shield = null;

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, search } = useLocation();

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // SoccerBible-style: the full wordmark rotates out for the short KSKF mark once you scroll past the masthead.
  // On the home page the header also steps aside before the footer, so the page ends without furniture over it.
  const [nearBottom, setNearBottom] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 90);
      const doc = document.documentElement;
      const fromBottom = doc.scrollHeight - (window.scrollY + window.innerHeight);
      setNearBottom(pathname === "/" && fromBottom < 640);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pathname]);


  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-[80] border-b border-white/10 bg-black text-white transition-all duration-500 ${
          nearBottom && !open && !searchOpen ? "pointer-events-none -translate-y-full opacity-0" : "translate-y-0 opacity-100"
        }`}
      >
        <div className="kikoy-bar h-[3px] w-full" aria-hidden="true" />
        <div className="relative mx-auto flex h-12 max-w-[1500px] items-center justify-between gap-3 px-3 md:h-14 md:px-6">
          <div className="flex min-w-0 items-center gap-2 md:gap-4">
            <Link
              to="/"
              aria-label="KickStartKenyaFootball home"
              data-testid="nav-logo"
              className="block shrink-0"
            >
              <span
                className={`relative block h-[16px] transition-[width] duration-500 ease-out md:h-[18px] ${
                  scrolled ? "w-[44px] sm:w-[52px] md:w-[58px]" : "w-[108px] sm:w-[150px] md:w-[168px]"
                }`}
              >
                <img
                  src="/brand/kskf-wordmark-white.png"
                  alt="KickStartKenyaFootball"
                  data-testid="nav-wordmark"
                  draggable="false"
                  className={`absolute left-0 top-1/2 h-[8px] w-auto max-w-none -translate-y-1/2 select-none transition-all duration-500 sm:h-[10px] md:h-[12px] ${
                    scrolled ? "scale-95 opacity-0" : "scale-100 opacity-100"
                  }`}
                />
                <img
                  src="/brand/kskf-short-white.png"
                  alt="KSKF"
                  data-testid="nav-wordmark-short"
                  draggable="false"
                  className={`absolute left-0 top-1/2 h-[8px] w-auto max-w-none -translate-y-1/2 select-none transition-all duration-500 sm:h-[10px] md:h-[11px] ${
                    scrolled ? "scale-100 opacity-100" : "scale-105 opacity-0"
                  }`}
                />
              </span>
            </Link>
            <span
              className="min-w-0 text-[0.56rem] leading-tight tracking-[0.01em] text-zinc-400 transition-all duration-500 sm:truncate sm:text-[0.68rem]"
              data-testid="nav-slogan"
            >
              Our Youth. Our Plan. Our Football.
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-1 md:gap-3" data-testid="nav-actions">
            <button
              type="button"
              aria-label="Search"
              data-testid="nav-search-btn"
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 text-ochre transition-colors hover:text-white"
            >
              <Search size={20} />
            </button>
            <span className="hidden h-5 w-px bg-ochre/40 lg:block" aria-hidden="true" />
            <Link
              to="/admin/login"
              data-testid="nav-login-btn"
              className="hidden text-xs font-bold uppercase tracking-wider text-ochre transition-colors hover:text-white lg:inline md:text-sm"
            >
              Login
            </Link>
            <Link
              to="/hall-of-fame/leaderboard"
              data-testid="nav-join-btn"
              className="rounded-none bg-maasai px-2.5 py-1 text-[0.55rem] font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black sm:px-4 sm:text-xs md:px-5 md:text-sm"
            >
              <span className="sm:hidden">HOF</span>
              <span className="hidden sm:inline">Hall of Fame Top 20</span>
            </Link>
            <button
              type="button"
              aria-label="Open menu"
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              className="hidden p-2 text-ochre transition-colors hover:text-white md:block"
            >
              <Menu size={26} strokeWidth={2.4} />
            </button>
          </div>
        </div>

        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-white/10 bg-[#0b0b0b]"
              data-testid="nav-search-form"
            >
              <div className="mx-auto flex max-w-[1500px] items-center gap-3 px-4 py-3 md:px-6">
                <PlayerSearch variant="nav" testId="nav-player-search" onClose={() => setSearchOpen(false)} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[95] overflow-y-auto bg-black text-white"
            data-testid="nav-drawer"
          >
            <div className="kikoy-bar h-[3px] w-full" aria-hidden="true" />
            <div className="mx-auto flex h-12 max-w-[1500px] items-center justify-between gap-3 px-3 md:h-14 md:px-6">
              <div className="flex min-w-0 items-center gap-2 md:gap-4">
                <Link to="/" onClick={() => setOpen(false)} aria-label="KickStartKenyaFootball home">
                  <img
                    src="/brand/kskf-wordmark-white.png"
                    alt="KickStartKenyaFootball"
                    data-testid="drawer-wordmark"
                    draggable="false"
                    className="h-[10px] w-auto max-w-none select-none md:h-[12px]"
                  />
                </Link>
                <span className="min-w-0 text-[0.56rem] leading-tight text-zinc-400 sm:truncate sm:text-[0.68rem]">
                  Our Youth. Our Plan. Our Football.
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1 md:gap-3">
                <button
                  type="button"
                  aria-label="Search"
                  data-testid="drawer-search-btn"
                  onClick={() => {
                    setOpen(false);
                    setSearchOpen(true);
                  }}
                  className="p-2 text-ochre transition-colors hover:text-white"
                >
                  <Search size={20} />
                </button>
                <button
                  type="button"
                  aria-label="Close menu"
                  data-testid="nav-drawer-close"
                  onClick={() => setOpen(false)}
                  className="p-2 text-ochre transition-colors hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="mx-auto max-w-[1500px] px-5 pb-12 pt-6 md:px-10 md:pt-10">
              <div className="grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {DRAWER.map((group, gi) => (
                  <motion.div
                    key={group.heading}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 + gi * 0.05, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <p className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-ochre">{group.heading}</p>
                    <ul className="mt-3 space-y-1.5">
                      {group.links.map(([label, to]) => (
                        <li key={label + to}>
                          <Link
                            to={to}
                            onClick={() => setOpen(false)}
                            data-testid={`drawer-link-${group.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="block text-sm leading-snug text-white transition-colors hover:text-ochre md:text-base"
                          >
                            {label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6">
                <Link
                  to="/hall-of-fame/leaderboard"
                  onClick={() => setOpen(false)}
                  className="btn-primary inline-flex"
                  data-testid="drawer-support-cta"
                >
                  Explore the Hall of Fame Top 20 <ArrowRight size={14} />
                </Link>
                <SocialRow testId="drawer-social" label="" />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};
